import React, { useState } from "react";
import {
  Button,
  HelperText,
  Label,
  Select,
  Spinner,
  Textarea,
  TextInput,
} from "flowbite-react";
import { AlertModal, useTranslation, useToast, useAuthentication } from "@repo/core";
import { useDeactivateAccount } from "@repo/api";
import {
  DEACTIVATION_REASON_KEYS,
  OTHER_REASON_KEY,
} from "../../utils/deactivationReasons";
import type { DeactivationReasonKey } from "../../utils/deactivationReasons";
import { extractDeactivateFieldErrors } from "../../utils/authErrors";
import type { DeactivateAccountFieldErrors } from "../../utils/authErrors";

/** Account deactivation, ported from anp-fe's DeleteAccountForm.vue: pick a
 * reason (free text when "other"), re-enter the current password, submit.
 *
 * Unlike anp-fe this asks for an explicit confirmation first - the action is
 * irreversible from the UI - and, since there's no /deactivated route here,
 * finishes by logging the user out rather than redirecting to one. */
const DeactivateAccountCard: React.FC = () => {
  const [reasonKey, setReasonKey] = useState<DeactivationReasonKey | "">("");
  const [otherReason, setOtherReason] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<DeactivateAccountFieldErrors>({});
  const [showConfirm, setShowConfirm] = useState(false);

  const { t } = useTranslation();
  const { addSuccessMessage, addErrorMessage } = useToast();
  const { logOut } = useAuthentication();
  const deactivateAccount = useDeactivateAccount();

  const isOther = reasonKey === OTHER_REASON_KEY;
  const canSubmit =
    reasonKey !== "" &&
    password.length > 0 &&
    (!isOther || otherReason.trim().length > 0);

  const handleRequestConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setShowConfirm(true);
  };

  const handleDeactivate = async () => {
    // anp-fe sends the free-text answer in place of the canned label when the
    // user picked "other", so the backend always receives a single `reason`.
    const reason = isOther
      ? otherReason.trim()
      : t(`profile.deactivate.reasons.${reasonKey}`);

    try {
      await deactivateAccount.mutateAsync({ password, reason });

      setShowConfirm(false);
      addSuccessMessage(t("profile.deactivate.success"));
      logOut();
    } catch (error) {
      console.error("Account deactivation failed:", error);
      setShowConfirm(false);
      const errors = extractDeactivateFieldErrors(error);
      if (Object.values(errors).some(Boolean)) {
        setFieldErrors(errors);
      }
      addErrorMessage(t("profile.deactivate.error"));
    }
  };

  return (
    <section className="rounded-lg border border-red-200 bg-white p-8 shadow dark:border-red-900 dark:bg-gray-800">
      <h3 className="text-lg font-semibold text-red-700 dark:text-red-400">
        {t("profile.deactivate.title")}
      </h3>
      <p className="mt-1 mb-6 text-sm text-gray-600 dark:text-gray-400">
        {t("profile.deactivate.description")}
      </p>

      <form className="space-y-4" onSubmit={handleRequestConfirm}>
        <div>
          <Label htmlFor="deactivationReason">{t("profile.deactivate.reasonLabel")}</Label>
          <Select
            id="deactivationReason"
            name="deactivationReason"
            required
            value={reasonKey}
            color={fieldErrors.reason ? "failure" : undefined}
            onChange={(e) => setReasonKey(e.target.value as DeactivationReasonKey | "")}
          >
            <option value="">{t("profile.deactivate.reasonPlaceholder")}</option>
            {DEACTIVATION_REASON_KEYS.map((key) => (
              <option key={key} value={key}>
                {t(`profile.deactivate.reasons.${key}`)}
              </option>
            ))}
          </Select>
          {fieldErrors.reason && (
            <HelperText color="failure">{fieldErrors.reason}</HelperText>
          )}
        </div>

        {isOther && (
          <div>
            <Label htmlFor="otherReason">{t("profile.deactivate.specificReasonLabel")}</Label>
            <Textarea
              id="otherReason"
              name="otherReason"
              rows={3}
              required
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
            />
          </div>
        )}

        <div>
          <Label htmlFor="deactivatePassword">{t("profile.deactivate.passwordLabel")}</Label>
          <TextInput
            id="deactivatePassword"
            name="deactivatePassword"
            type="password"
            autoComplete="current-password"
            required
            color={fieldErrors.password ? "failure" : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {fieldErrors.password && (
            <HelperText color="failure">{fieldErrors.password}</HelperText>
          )}
        </div>

        <Button
          type="submit"
          color="red"
          className="w-full"
          disabled={deactivateAccount.isPending || !canSubmit}
        >
          {deactivateAccount.isPending && <Spinner size="sm" className="mr-2" light />}
          {t("profile.deactivate.submit")}
        </Button>
      </form>

      <AlertModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        title={t("profile.deactivate.confirmTitle")}
        message={t("profile.deactivate.confirmMessage")}
        confirmText={t("profile.deactivate.submit")}
        confirmColor="red"
        isDisabled={deactivateAccount.isPending}
        onConfirm={handleDeactivate}
      />
    </section>
  );
};

export default DeactivateAccountCard;
